import Decimal from 'decimal.js-light'
import { v4 as uuidV4 } from 'uuid'
import discountTypes from '../dominio/discount-types'
import payBoxTypes from '../dominio/pay-box-types'

/**
 * Utilitários para o tipo de promoção `SpendAndGet`.
 *
 * Filosofia: o backend é a fonte da verdade. Estes utilitários replicam a
 * regra apenas para que o front possa antecipar a inserção dos brindes no
 * carrinho conforme o cliente atinge a tier. O backend valida na confirmação.
 */
const spendAndGetUtils = {
  /**
   * Soma o `netValue` dos itens ativos que NÃO são brindes de uma das
   * promoções `SpendAndGet` ativas — seguindo a regra do
   * `Eligible_Order_Amount` (Requirement 5.1, 5.3, 5.4).
   *
   * @param {Array} cartItems — `state.items` do pay-box
   * @param {Set<number>|number[]} activeSpendAndGetPromotionIds — ids das
   *   promoções SpendAndGet ativas; itens cujo `promotionId` cair neste
   *   conjunto são tratados como brinde e excluídos da soma.
   * @returns {number} valor elegível com 2 casas decimais
   */
  calculateEligibleAmount(cartItems, activeSpendAndGetPromotionIds) {
    if (!cartItems || cartItems.length === 0) return 0

    const promotionIdsSet =
      activeSpendAndGetPromotionIds instanceof Set
        ? activeSpendAndGetPromotionIds
        : new Set(activeSpendAndGetPromotionIds || [])

    const sum = cartItems.reduce((acc, item) => {
      if (item.isDeleted) return acc
      const itemPromotionId = item.skuProduct?.promotionId ?? item.promotionId
      // Brindes (item com promotionId apontando para uma SpendAndGet ativa) não contam
      if (itemPromotionId && promotionIdsSet.has(itemPromotionId)) return acc

      const localNetValue =
        item.priceRuleSelected?.priceInfo?.localNetValue ?? item.netValue ?? 0
      return acc.add(localNetValue)
    }, new Decimal(0))

    return Number(sum.toFixed(2))
  },

  /**
   * Identifica a `Applied_Tier`: a tier de maior `minimumAmount` cujo
   * `minimumAmount <= eligibleAmount`. Retorna `null` quando nenhuma faixa
   * é elegível (Requirements 6.1, 6.2).
   *
   * @param {Object} promotion — payload `PayBoxSpendAndGetPromotionDto`
   * @param {number} eligibleAmount
   * @returns {Object|null} a tier aplicada ou null
   */
  selectAppliedTier(promotion, eligibleAmount) {
    if (!promotion?.tiers?.length) return null

    return promotion.tiers
      .filter(tier => Number(tier.minimumAmount) <= eligibleAmount)
      .reduce(
        (max, tier) => (max === null || Number(tier.minimumAmount) > Number(max.minimumAmount) ? tier : max),
        null
      )
  },

  /**
   * Constrói o `saleItem` de brinde no formato consumido pelo
   * `pay-box.SET_ITEMS`. Marca-o com flag local `isReward = true` para que
   * a UI esconda controles de quantidade. O backend valida estes campos:
   *
   * - `promotionId` = id da promoção SpendAndGet
   * - `quantity` = exato configurado no reward
   * - `netValue` = 0 (anti-tampering)
   * - `unitDiscount = unitValue` e `discountType = VALUE`
   *
   * @param {Object} promotion — `PayBoxSpendAndGetPromotionDto`
   * @param {Object} tier — `PayBoxSpendAndGetTierDto`
   * @param {Object} reward — `PayBoxSpendAndGetRewardDto`
   * @param {Object} [options]
   * @param {number|null} [options.priceTableId] — id da price-table da venda em curso
   *   (necessário para que o mapper de finalização monte o payload corretamente)
   * @returns {Object} saleItem pronto para `localListAddItem`
   */
  buildRewardSaleItem(promotion, tier, reward, { priceTableId = null } = {}) {
    const sku = reward.sku || {}
    const unitValue = Number(sku.price || 0)
    const quantity = Number(reward.quantity)
    const grossValue = new Decimal(unitValue).mul(quantity).toNumber()
    const { VALUE } = discountTypes.computed.discountTypesEnum()

    return {
      localId: uuidV4(),
      id: null,
      promotionId: promotion.id,
      priceTable: { id: priceTableId, discount: 0 },
      priceTableId,

      skuProduct: {
        id: sku.id,
        skuId: sku.id,
        name: sku.name,
        promotionId: promotion.id,
        price: unitValue,
        originalPrice: sku.fullPrice ?? unitValue,
        unitValue,
        stock: sku.stock,
        imageSquare: sku.imageSquare,
        imageWide: sku.imageWide,
        kit: false,
        productKitId: null,
        ean: null,
        customerRequired: false,
      },

      productKitId: null,
      saleItemKitId: null,
      kitCreateUuid: null,
      saleItemAssociatedId: null,
      skuAssociatedId: null,
      skuTieredPriceId: null,
      choppGiftCardId: null,
      palmBeerCardNumber: null,
      choppGiftCardNumber: null,

      quantity,
      orderQuantity: 0,
      maxQuantity: quantity,

      unitValue,
      price: unitValue,
      originalUnitValue: unitValue,
      unitDiscount: unitValue,
      discountType: VALUE,
      grossValue,
      netValue: 0,
      contractualFine: 0,
      discountPriceTable: 0,

      inputMethod: payBoxTypes.data().productInputMethodEnum.SEARCHED,
      classification: null,

      isDeleted: false,
      isSuggestion: false,
      replicatedWithoutPromotion: false,

      // Defaults defensivos consumidos pelo PayBox/ATM ao renderizar/iterar.
      kitItems: [],
      lendingProducts: [],
      tieredPrices: [],

      // Flags locais (não-persistidas) usadas pela UI e pelo reprocessador
      isReward: true,
      spendAndGetTierId: tier.id,
      spendAndGetRewardId: reward.id,

      priceRuleSelected: {
        type: 'SPEND_AND_GET_REWARD',
        priceTable: { id: priceTableId, discount: 0 },
        productKit: null,
        promotion: {
          id: promotion.id,
          name: promotion.name,
        },
        tieredPrice: null,
        maxQuantity: quantity,
        priceInfo: {
          price: unitValue,
          oldPrice: sku.fullPrice ?? unitValue,
          hasOldPrice: !!(sku.fullPrice && sku.fullPrice !== unitValue),
          localNetValue: 0,
          localGrossValue: grossValue,
          localDiscountTotal: grossValue,
          originalPrice: sku.fullPrice ?? unitValue,
          unitValue,
          discountType: VALUE,
          unitDiscount: unitValue,
          unitDiscountValue: unitValue,
          contractualFine: 0,
        },
      },
      priceRules: [],
    }
  },

  /**
   * Verifica se um saleItem do carrinho é um brinde de promoção
   * `SpendAndGet`. Aceita tanto a flag local `isReward` (item recém
   * inserido pelo reprocessador) quanto a recuperação por `promotionId`
   * presente no conjunto de promoções ativas (item carregado do banco).
   */
  isSpendAndGetReward(saleItem, activeSpendAndGetPromotionIds) {
    if (!saleItem || saleItem.isDeleted) return false
    if (saleItem.isReward) return true

    const promotionId = saleItem.skuProduct?.promotionId ?? saleItem.promotionId
    if (!promotionId) return false

    const set =
      activeSpendAndGetPromotionIds instanceof Set
        ? activeSpendAndGetPromotionIds
        : new Set(activeSpendAndGetPromotionIds || [])

    return set.has(promotionId)
  },
}

export default spendAndGetUtils
